import React, { useMemo } from "react";

const Card = ({
  title,
  percentage,
  comparisonText,
  showComparison = true,
  mainValue,
  onButtonClick,
  buttonText = "View",
  customStyles = "",
  href,
  onClick,
  loading = false,
  density = "comfortable",
  trend,
  intent,
  trendData = [],
  tooltip,
}) => {
  const pctNum = useMemo(() => {
    if (percentage === null || percentage === undefined || percentage === "") return null;
    if (typeof percentage === "number") return percentage;
    const n = Number(String(percentage).replace("%", "").replace(/\s+/g, ""));
    return Number.isNaN(n) ? null : n;
  }, [percentage]);

  const autoTrend = useMemo(() => {
    if (trend) return trend;
    if (pctNum === null) return "flat";
    if (pctNum > 0) return "up";
    if (pctNum < 0) return "down";
    return "flat";
  }, [trend, pctNum]);

  const autoIntent = useMemo(() => {
    if (intent) return intent;
    if (autoTrend === "up") return "positive";
    if (autoTrend === "down") return "negative";
    return "neutral";
  }, [intent, autoTrend]);

  const palette = useMemo(() => {
    switch (autoIntent) {
      case "positive":
        return {
          chipBg: "bg-green-100 dark:bg-green-900/40",
          chipText: "text-green-800 dark:text-green-300",
          accent: "text-green-700 dark:text-green-400",
        };
      case "negative":
        return {
          chipBg: "bg-red-100 dark:bg-red-900/40",
          chipText: "text-red-800 dark:text-red-300",
          accent: "text-red-700 dark:text-red-400",
        };
      default:
        return {
          chipBg: "bg-gray-100 dark:bg-gray-700/50",
          chipText: "text-gray-700 dark:text-gray-300",
          accent: "text-gray-600 dark:text-gray-400",
        };
    }
  }, [autoIntent]);

  const pad = density === "compact" ? "p-fluid-1" : "p-fluid-1.5";
  const gap = density === "compact" ? "gap-fluid-0.5" : "gap-fluid-0.75";

  const Wrapper = href ? "a" : "div";
  const wrapperProps = href ? { href, onClick } : { onClick };

  const Arrow = ({ dir = "up" }) => {
    const cls =
      dir === "up" ? "rotate-0" : dir === "down" ? "rotate-180" : "-rotate-90";
    return (
      <svg
        viewBox="0 0 20 20"
        className={`h-3.5 w-3.5 ${cls}`}
        aria-hidden="true"
      >
        <path d="M10 4l6 9H4l6-9z" className={palette.accent} />
      </svg>
    );
  };

  return (
    <Wrapper
      {...wrapperProps}
      title={tooltip}
      className={`group relative w-[clamp(160px,16vw,220px)] ${pad}
        bg-white dark:bg-acs-dark-surface rounded-xl
        border border-gray-200 dark:border-acs-dark-border
        shadow-sm flex flex-col justify-between ${gap} ${customStyles}
        transition-none
        ${onClick || href ? "cursor-pointer" : "cursor-default"}`}
      tabIndex={onClick || href ? 0 : -1}
    >
      {/* Title & Main Value */}
      <div className="flex flex-col">
        <h2 className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext font-roboto font-medium leading-none">
          {title}
        </h2>
        <p className="text-fluid-xl text-gray-900 dark:text-acs-dark-text font-roboto font-bold mt-fluid-0.25">
          {mainValue ?? "—"}
        </p>
      </div>

      {showComparison && (
        <div className={`flex items-center justify-between mt-fluid-1 ${gap}`}>
          <div className="flex items-center gap-fluid-1 min-w-0">
            <div
              className={`inline-flex items-center gap-1 px-fluid-1 py-[0.15rem] rounded-full
                          ${palette.chipBg} ${palette.chipText} font-inter text-fluid-xs font-semibold`}
            >
              <Arrow dir={autoTrend} />
              <span className="tabular-nums">
                {percentage !== undefined && percentage !== null && percentage !== ""
                  ? String(percentage)
                  : "—"}
              </span>
            </div>
            <span className="text-gray-500 dark:text-acs-dark-subtext text-fluid-xs font-inter font-medium truncate">
              {comparisonText}
            </span>
          </div>

          {buttonText && (
            <button
              className="text-fluid-xs px-fluid-button-x py-fluid-button-y border border-gray-200 dark:border-acs-dark-border
                         rounded-md text-gray-800 dark:text-acs-dark-text font-roboto font-medium
                         hover:bg-gray-50 dark:hover:bg-acs-dark-hover transition-none"
              onClick={(e) => {
                e.stopPropagation();
                onButtonClick?.(e);
              }}
              type="button"
            >
              {buttonText}
            </button>
          )}
        </div>
      )}
    </Wrapper>
  );
};

export default Card;
